import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { styled, useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  Breadcrumbs,
} from '@mui/material';
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  DashboardOutlined as DashboardIcon,
  Settings as SettingsIcon,
  Security as PrivacyIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AssessmentOutlined as HealthIcon,
  BusinessOutlined as BusinessIcon,
  GroupOutlined as TeamIcon,
  HomeOutlined as HomeIcon,
  FolderOutlined as ContentIcon,
  ViewModuleOutlined as ViewsIcon,
  LinkOutlined as LinkIcon,
  LocalOfferOutlined as TagIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useOrganization } from '../../context/OrganizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../utils/permissions';
import CreateTeamDialog from '../organization/CreateTeamDialog';
import { teamService } from '../../services/team/team.service';
import { ApiError } from '../../utils/error-handler';
import WorkspaceFrame from './WorkspaceFrame';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: open ? drawerWidth : 0,
  width: '100%',
  maxWidth: open ? `calc(100% - ${drawerWidth}px)` : '100%',
}));

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{
  open?: boolean;
}>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: '100%',
  zIndex: theme.zIndex.drawer + 1,
}));

/** Shared sx for every nav ListItemButton in the sidebar. */
const navItemSx = (theme: Theme): SystemStyleObject<Theme> => ({
  color: 'rgba(255,255,255,0.72)',
  background: 'transparent',
  borderRadius: '8px',
  padding: '6px 12px',
  margin: '0 0 2px 0',
  minHeight: 'auto',
  fontSize: '13.5px',
  fontWeight: 600,
  position: 'relative',
  '&:hover': { background: 'rgba(255,255,255,0.05)', color: '#fff' },
  '&.Mui-selected': {
    background: 'rgba(34,181,220,0.14)',
    color: '#fff',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 2,
      top: 6,
      bottom: 6,
      width: 3,
      borderRadius: 3,
      background: theme.palette.brand.cyan,
    },
    '& .MuiListItemIcon-root': { color: theme.palette.brand.cyan },
  },
  '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.72)', minWidth: 26 },
  '& .MuiListItemIcon-root .MuiSvgIcon-root': { fontSize: 17 },
  '& .MuiListItemText-root': { margin: 0 },
  '& .MuiListItemText-primary': { fontSize: '13.5px', fontWeight: 600, lineHeight: 1.4 },
});

const SidebarLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const muiTheme = useMuiTheme();
  const { effectiveMode, setThemeMode } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Check if we're on an editor route that needs full width
  const isEditorRoute =
    location.pathname.includes('/views/create') ||
    (location.pathname.includes('/views/') && location.pathname.includes('/edit'));

  // Suppress the navigation-history breadcrumb on the view renderer route
  // so the reader gets a clean reading experience. The view's own header carries
  // the title.
  const isViewRendererRoute =
    /^\/views\/[^/]+$/.test(location.pathname) &&
    !location.pathname.endsWith('/edit') &&
    !location.pathname.endsWith('/create');

  const {
    organizations,
    teams,
    currentOrganizationId,
    currentTeamId,
    setCurrentOrganizationId,
    setCurrentTeamId,
    subscriptions,
    refreshData,
  } = useOrganization();
  const { hasPermission } = usePermissions();

  const [open, setOpen] = useState(!isMobile);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);

  // Navigation history - track visited pages
  interface HistoryEntry {
    path: string;
    label: string;
    timestamp: number;
  }
  const [navigationHistory, setNavigationHistory] = useState<HistoryEntry[]>([]);
  const maxHistorySize = 10;
  const lastPathnameRef = useRef<string>(location.pathname);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setUserMenuAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchorEl(null);

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/login');
  };

  const handleCreateTeam = async (data: {
    name: string;
    description: string;
    subscriptionId: string;
  }) => {
    try {
      const team = await teamService.createTeam({
        name: data.name,
        description: data.description,
        organizationId: currentOrganizationId!,
        subscriptionId: data.subscriptionId,
      });
      await refreshData();
      navigate(`/organizations/${currentOrganizationId}/teams/${team.id || team._id}`);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || 'Failed to create team',
          code: 'UNKNOWN_ERROR',
          status: 500,
        } as ApiError,
      };
    }
  };

  // Generate friendly label for a path
  const generatePageLabel = (pathname: string): string => {
    const paths = pathname.split('/').filter((path) => path);

    if (paths.length === 0 || pathname === '/dashboard') return 'Dashboard';
    if (pathname === '/content') return 'Content';
    if (pathname === '/views') return 'View Editor';
    if (pathname === '/links') return 'Links';
    if (pathname === '/tags') return 'Tags';
    if (pathname === '/settings') return 'Settings';
    if (pathname === '/profile') return 'Profile';
    if (pathname === '/organizations/new') return 'New Organization';

    const orgMatch = pathname.match(/^\/organizations\/([^/]+)$/);
    if (orgMatch) {
      const org = organizations.find((o) => o.id === orgMatch[1]);
      return org ? org.name : 'Organization';
    }

    const teamMatch = pathname.match(/^\/organizations\/([^/]+)\/teams\/([^/]+)$/);
    if (teamMatch) {
      const team = teams.find((t) => t.id === teamMatch[2]);
      return team ? team.name : 'Team';
    }

    if (pathname.includes('/content/')) return 'Content Details';
    if (pathname.includes('/views/create')) return 'Create View';
    if (pathname.includes('/views/') && pathname.includes('/edit')) return 'Edit View';

    return paths[0].charAt(0).toUpperCase() + paths[0].slice(1);
  };

  // Track navigation history
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath !== lastPathnameRef.current) {
      const label = generatePageLabel(currentPath);
      const newEntry: HistoryEntry = { path: currentPath, label, timestamp: Date.now() };
      setNavigationHistory((prev) => {
        if (prev.length > 0 && prev[prev.length - 1].path === currentPath) return prev;
        return [...prev, newEntry].slice(-maxHistorySize);
      });
      lastPathnameRef.current = currentPath;
    }
  }, [location.pathname, organizations, teams]);

  // Generate navigation history breadcrumbs
  const generateBreadcrumbs = () => {
    if (navigationHistory.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link
            to="/dashboard"
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              color:
                effectiveMode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
            }}
          >
            <HomeIcon fontSize="small" />
          </Link>
          <Typography
            variant="body2"
            sx={{
              color:
                effectiveMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {generatePageLabel(location.pathname)}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Link
          to="/dashboard"
          style={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            color:
              effectiveMode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
          }}
        >
          <HomeIcon fontSize="small" />
        </Link>

        <Breadcrumbs
          aria-label="navigation history"
          maxItems={5}
          itemsBeforeCollapse={2}
          itemsAfterCollapse={2}
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color:
                effectiveMode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          {navigationHistory.map((entry, index) => {
            const isLast = index === navigationHistory.length - 1;
            const isCurrent = entry.path === location.pathname;

            return isLast || isCurrent ? (
              <Typography
                key={`${entry.path}-${entry.timestamp}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color:
                    effectiveMode === 'dark'
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(0, 0, 0, 0.87)',
                  fontWeight: 500,
                }}
              >
                {entry.label}
              </Typography>
            ) : (
              <Link
                key={`${entry.path}-${entry.timestamp}`}
                to={entry.path}
                style={{
                  textDecoration: 'none',
                  color:
                    effectiveMode === 'dark'
                      ? 'rgba(255, 255, 255, 0.6)'
                      : 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                }}
              >
                {entry.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>
    );
  };

  // Helper function to check if path is selected
  const isPathSelected = (path: string) => {
    if (location.pathname === path) return true;
    if (
      (path === '/profile' || path === '/settings') &&
      (location.pathname === '/profile' || location.pathname === '/settings')
    ) {
      return true;
    }
    if (path.startsWith('/organizations/') && location.pathname.startsWith(path)) {
      return location.pathname === path;
    }
    if (
      path.startsWith('/organizations/') &&
      path.includes('/teams/') &&
      location.pathname === path
    ) {
      return true;
    }
    return false;
  };

  const currentOrg = organizations.find((org) => org.id === currentOrganizationId) ?? null;
  const currentTeam = teams.find((team) => team.id === currentTeamId) ?? null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AppBarStyled
        position="fixed"
        color="default"
        elevation={0}
        sx={{ width: '100%' }}
      >
        <Toolbar disableGutters sx={{ px: 1.5 }}>
          <IconButton
            color="inherit"
            aria-label={open ? 'Close drawer' : 'Open drawer'}
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            sx={{ mr: 1 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>

          {/* Logo cell — stays on topbar surface, never inherits navy sidebar color */}
          <Box
            component={Link}
            to="/"
            sx={(theme) => ({
              flexShrink: 0,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1.5,
              width: 232,
              height: '100%',
              paddingLeft: 2.25,
              paddingRight: 2.25,
              boxSizing: 'border-box',
              background: 'inherit',
              borderRight: `1px solid ${theme.palette.divider}`,
              textDecoration: 'none',
              color: 'inherit',
              mr: 2,
            })}
          >
            <Box
              component="img"
              src="/assets/tier-one-content_icon_dynamic-layers_128.png"
              alt="Tier One Content"
              sx={{ width: 28, height: 28, flexShrink: 0 }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.01em' }} noWrap>
              Tier One Content
            </Typography>
          </Box>

          {/* Breadcrumbs — suppressed on view renderer route for clean reading experience */}
          {!isViewRendererRoute && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 4 }}>
              {generateBreadcrumbs()}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Right-side topbar controls */}
          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

              {/* Theme toggle pill */}
              <Box sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                padding: '4px 8px',
                borderRadius: '8px',
                background: theme.palette.action.hover,
              })}>
                <IconButton
                  size="small"
                  onClick={() => setThemeMode('light')}
                  aria-label="Light mode"
                  aria-pressed={effectiveMode === 'light'}
                  sx={(theme) => ({
                    padding: '4px',
                    color: effectiveMode === 'light' ? theme.palette.brand.teal : theme.palette.text.secondary,
                    opacity: effectiveMode === 'light' ? 1 : 0.55,
                    '&:hover': { background: 'transparent', opacity: 1 },
                  })}
                >
                  <LightModeIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setThemeMode('dark')}
                  aria-label="Dark mode"
                  aria-pressed={effectiveMode === 'dark'}
                  sx={(theme) => ({
                    padding: '4px',
                    color: effectiveMode === 'dark' ? theme.palette.brand.cyan : theme.palette.text.secondary,
                    opacity: effectiveMode === 'dark' ? 1 : 0.55,
                    '&:hover': { background: 'transparent', opacity: 1 },
                  })}
                >
                  <DarkModeIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>

              {/* Notification bell */}
              <IconButton sx={(theme) => ({
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: theme.palette.action.hover,
                color: theme.palette.text.secondary,
                '&:hover': { background: theme.palette.action.selected },
              })}>
                <NotificationsIcon sx={{ fontSize: 15 }} />
              </IconButton>

              {/* User pill */}
              <Box
                onClick={handleUserMenuOpen}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  padding: '4px 10px 4px 12px',
                  borderRadius: '999px',
                  background: theme.palette.action.hover,
                  cursor: 'pointer',
                  '&:hover': { background: theme.palette.action.selected },
                })}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                  {user?.name || user?.email}
                </Typography>
                <Avatar sx={(theme) => ({
                  width: 26,
                  height: 26,
                  background: theme.palette.brand.teal,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                })}>
                  {user?.name
                    ? user.name.charAt(0).toUpperCase()
                    : user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              </Box>

              {/* User dropdown menu — preserves settings/privacy/logout actions */}
              <Menu
                id="menu-appbar"
                anchorEl={userMenuAnchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleUserMenuClose}
              >
                <MenuItem
                  component={Link}
                  to="/settings"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
                <MenuItem
                  component={Link}
                  to="/privacy-center"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <PrivacyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Privacy Center</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBarStyled>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            marginTop: '64px',
            height: 'calc(100% - 64px)',
            display: 'flex',
            flexDirection: 'column',
            background: muiTheme.palette.brand.navy,
            backgroundColor: `${muiTheme.palette.brand.navy} !important`,
            color: 'rgba(255,255,255,0.78)',
            border: 'none',
            boxShadow: 'none',
            padding: '14px 12px 12px',
          },
          '& .MuiList-root, & .MuiListItem-root, & .MuiListItemButton-root:not(.Mui-selected):not(:hover)': {
            background: 'transparent',
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        {/* Workspace Frame */}
        <WorkspaceFrame
          currentOrg={currentOrg}
          currentTeam={currentTeam}
          onCreateTeam={() => setCreateTeamDialogOpen(true)}
          canCreateTeam={Boolean(currentOrganizationId)}
          drawerWidth={drawerWidth}
        />

        {/* Divider below workspace selector block */}
        <Box sx={{ height: '1px', background: 'rgba(255,255,255,0.22)', margin: '12px 4px 0 4px', flexShrink: 0 }} />

        {/* Content navigation links */}
        <List sx={{ padding: '4px 0', background: 'transparent', margin: '0' }}>
          {/* Dashboard */}
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/dashboard"
              selected={isPathSelected('/dashboard')}
              sx={navItemSx}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>

          {/* Content — only when a team is selected */}
          {currentTeamId && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/content"
                selected={isPathSelected('/content')}
                sx={navItemSx}
              >
                <ListItemIcon>
                  <ContentIcon />
                </ListItemIcon>
                <ListItemText primary="Content" />
              </ListItemButton>
            </ListItem>
          )}

          {/* Links — only when a team is selected and user can share content */}
          {currentTeamId && hasPermission(PERMISSIONS.CONTENT_SHARE) && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/links"
                selected={isPathSelected('/links')}
                sx={navItemSx}
              >
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                <ListItemText primary="Links" />
              </ListItemButton>
            </ListItem>
          )}

          {/* Tags — only when a team is selected and user can create content */}
          {currentTeamId && hasPermission(PERMISSIONS.CONTENT_CREATE) && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/tags"
                selected={isPathSelected('/tags')}
                sx={navItemSx}
              >
                <ListItemIcon>
                  <TagIcon />
                </ListItemIcon>
                <ListItemText primary="Tags" />
              </ListItemButton>
            </ListItem>
          )}

          {/* View Editor — only when content is visible and user can publish */}
          {currentTeamId && hasPermission(PERMISSIONS.CONTENT_PUBLISH) && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/views"
                selected={isPathSelected('/views')}
                sx={navItemSx}
              >
                <ListItemIcon>
                  <ViewsIcon />
                </ListItemIcon>
                <ListItemText primary="View Editor" />
              </ListItemButton>
            </ListItem>
          )}
        </List>

        {/* Spacer to push bottom items down */}
        <Box sx={{ flexGrow: 1, background: 'transparent' }} />

        {/* Divider between content and admin sections */}
        <Box sx={{ height: '1px', flexShrink: 0, background: 'rgba(255,255,255,0.22)', margin: '0 4px' }} />

        {/* Administration navigation links */}
        <List sx={{ padding: '4px 0', background: 'transparent', margin: '0' }}>
          {/* Organization — only show when user has explicit membership */}
          {currentOrganizationId && currentOrg && !currentOrg.isImplicit && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to={`/organizations/${currentOrganizationId}`}
                selected={isPathSelected(`/organizations/${currentOrganizationId}`)}
                sx={navItemSx}
              >
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary="Organization" />
              </ListItemButton>
            </ListItem>
          )}

          {currentTeamId && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to={`/organizations/${currentOrganizationId}/teams/${currentTeamId}`}
                selected={isPathSelected(
                  `/organizations/${currentOrganizationId}/teams/${currentTeamId}`
                )}
                sx={navItemSx}
              >
                <ListItemIcon>
                  <TeamIcon />
                </ListItemIcon>
                <ListItemText primary="Team" />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem disablePadding>
            <ListItemButton
              onClick={() => window.open('/status', '_blank', 'noopener,noreferrer')}
              sx={navItemSx}
            >
              <ListItemIcon>
                <HealthIcon />
              </ListItemIcon>
              <ListItemText primary="System Status" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          marginTop: '64px',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Main
          open={open}
          sx={{
            padding: isEditorRoute ? 0 : muiTheme.spacing(3),
          }}
        >
          {children}
        </Main>
      </Box>

      {/* Create Team Dialog */}
      <CreateTeamDialog
        open={createTeamDialogOpen}
        onClose={() => setCreateTeamDialogOpen(false)}
        onSubmit={handleCreateTeam}
        organizationId={currentOrganizationId || ''}
      />
    </Box>
  );
};

export default SidebarLayout;
